import { nanoid } from 'nanoid';
import createClient, { createFinalURL, defaultQuerySerializer } from 'openapi-fetch';
import { localStorageService } from '$lib/services/localStorage';

import { API_BASE_URI } from '@components/env';
import { Cookie } from '@shared/cookies';
import { getCookie, setCookie } from '@utils/cookies';

import type { paths } from './swagger';
import type { GetKey, GetParams } from './types';

import { browser } from '$app/environment';
import type { PathsWithMethod } from 'openapi-typescript-helpers';

export type Client<T extends object = paths> = ReturnType<typeof createClient<T>>;
let fingerprint: Promise<string>;

export const getClient = <T extends object>(
    baseUrl: string,
    fetchFun?: typeof fetch,
    headers: Record<string, string> = {},
    catchFun: (e: unknown) => void = console.error
): Client<T> => {
    if (!browser && !fetchFun) {
        console.trace('Error fetch server side');
    }

    return catchClient(createClient<T>({
        fetch: fetchFun,
        baseUrl,
        credentials: 'include',
        headers: {
            ...headers,
            'cache-control': 'no-cache',
            'x-trace-id': nanoid(6),
            'X-XSS-Protection': '1; mode=block',
        },
    }), catchFun);
};

function catchClient<T extends object>(c: Client<T>, catchFun: (e: unknown) => void): Client<T> {
    const originalDelete = c.DELETE.bind(c);

    c.DELETE = (url, init) => {
        return originalDelete(url, init)
            .then(response => {
                if (response.response.status >= 400) {
                    throw response.error;
                }
                return response;
            }).catch((e: unknown) => {
                catchFun(e);
                throw e;
            });
    };
    const originalPatch = c.PATCH.bind(c);

    c.PATCH = (url, init) => {
        return originalPatch(url, init)
            .then(response => {
                if (response.response.status >= 400) {
                    throw response.error;
                }
                return response;
            }).catch((e: unknown) => {
                catchFun(e);
                throw e;
            });
    };
    const originalPost = c.POST.bind(c);

    c.POST = (url, init) => {
        return originalPost(url, init)
            .then(response => {
                if (response.response.status >= 400) {
                    throw response.error;
                }
                return response;
            }).catch((e: unknown) => {
                catchFun(e);
                throw e;
            });
    };
    return c;
}

export const initFingerprint = async () => {
    if (!browser) {
        return undefined;
    }
    const version = '1_';
    let fp = localStorageService.getItem('fp') ?? getCookie(Cookie.Fp);
    if (!fp || fp.substring(0, version.length) !== version) {
        const newFp = await getFingerprint();
        fp = `${version}${newFp}`;
    }

    if (fp) {
        localStorageService.setItem('fp', fp);
        setCookie(Cookie.Fp, fp);
    }
};

export const getFingerprint = async () => {
    try {
        if (!fingerprint) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const fingerPrint = await import('@fingerprintjs/fingerprintjs/fingerprint2');
            // eslint-disable-next-line
            fingerprint = new Promise((resolve) => fingerPrint.getV18({
                extraComponents: [
                    {
                        key: 'screen2',
                        getData: (done: (d: object) => void) => {
                            done(window.screen);
                        },
                    },
                    {
                        key: 'language2',
                        getData: (done: (d: object) => void) => {
                            done({ languages: window.navigator.languages.join(',') });
                        },
                    },
                ],
            }, resolve));
        }
        return fingerprint;
    } catch {
        return undefined;
    }
};

export function getClientUrl<Paths extends NonNullable<unknown>>() {
    return <P extends PathsWithMethod<Paths, 'get'> = PathsWithMethod<Paths, 'get'>>(
        url: P,
        query?: GetParams<GetKey<GetKey<Paths, P>, 'get'>>,
        path?: Record<string, unknown>
    ) => createFinalURL<P>(url as string, {
        querySerializer: defaultQuerySerializer,
        params: { query, path },
        baseUrl: API_BASE_URI,
    });
}
